import React, { useEffect, useState, useRef, useCallback } from 'react'
import { Input, Row, Col, Spin, Pagination, TreeSelect } from 'antd'
import { connect, useDispatch } from 'react-redux'
import { fetchTpoStudentList, getAssessmentDomains, getAssessmentDegrees, getAssessmentDepartments, getAssessmentSpecializations, exportTpoStudentListToExcel } from '../actions'
import {
  selectTpoStudentList,
  selectTpoStudentListPagination,
  selectTpoStudentListLoading,
} from '../selectors'
import Button from 'components/Button'
import { StyledTableSqare } from 'components/TableStyles/TableStylesSqare/TableStylesSqare'
import { ArrowLeftOutlined, SearchOutlined, CaretUpOutlined, CaretDownOutlined, DownloadOutlined } from '@ant-design/icons'
import {
  SearchSection,
  ShowingText,
  TableTop,
} from '../Style/style'
import { COLORS } from 'theme'
import styled from 'styled-components'
import StudentProgressDrawer from '../components/StudentProgressDrawer'

// --- Styled Components copied/adapted from CandidateList ---

const CandidateListWrapper = styled.div`
  padding: 12px;
  border-radius: 8px;
  width: 100%;
  max-width: 100%;
  overflow-x: hidden;
`

const HeaderFullWidth = styled.div`
  width: 100%;
  max-width: 100%;
`

const FilterSection = styled.div`
  margin-bottom: 0px;
`

const FilterInlineContainer = styled.div`
  padding: 24px 0;
  margin-bottom: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
  
  @media (max-width: 1200px) {
    padding: 20px 0;
  }
`

const FilterGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 24px;

  @media (max-width: 768px) {
    gap: 16px;
    padding: 0 16px;
  }
`

const FilterGroup = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  
  .ant-form-item {
    margin-bottom: 0;
  }
  
  .ant-select,
  .ant-tree-select {
    border-radius: 8px;
    border: 1px solid #d1d5db;
    transition: all 0.2s ease;
    min-width: 160px;
    max-width: 180px;
    
    &:hover {
      border-color: #3249d7;
      box-shadow: 0 0 0 2px rgba(50, 73, 215, 0.1);
    }
    
    &:focus,
    &.ant-select-focused,
    &.ant-tree-select-focused {
      border-color: #3249d7;
      box-shadow: 0 0 0 3px rgba(50, 73, 215, 0.15);
    }
  }
`

const StyledFormItem = styled.div`
  .ant-select {
    width: 100%;
  }
`

const EnhancedFilterLabel = styled.label`
  font-weight: 600;
  color: #374151;
  font-size: 13px;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
`

const StyledSearchInput = styled(Input)`
  height: 48px;
  border: none;
  box-shadow: none !important;
  
  &:focus,
  &:hover,
  &.ant-input-affix-wrapper-focused {
    border: none;
    box-shadow: none !important;
  }
  
  .ant-input {
    height: 100%;
    border: none;
    box-shadow: none !important;
    
    &:focus {
      box-shadow: none !important;
    }
  }
`

const AlignedTableTop = styled(TableTop)`
  margin-bottom: 0;
  
  ${SearchSection} {
    margin-bottom: 0;
  }
  
  ${ShowingText} {
    margin-bottom: 0;
  }
`

const NoData = styled.div`
  text-align: center;
  padding: 40px;
  color: #6b7280;
`

const SortHeader = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  gap: 4px;

  &:hover {
    color: #1890ff;
  }
`

const SortIcons = styled.span`
  display: inline-flex;
  flex-direction: column;
  font-size: 10px;
  line-height: 1;
  .active {
    color: #1890ff;
  }
  .inactive {
    color: #bfbfbf;
  }
`

// Helper to structure data for TreeSelect
const buildTreeData = (data, selectAllLabel = 'Select All') => {
  if (!data || data.length === 0) return []

  // Filter out invalid entries
  const validData = data.filter(item => {
    const name = item?.name || item?.label || ''
    const trimmedName = String(name).trim()
    return trimmedName && trimmedName !== '-' && trimmedName !== '---' && trimmedName.toLowerCase() !== 'null'
  })

  if (validData.length === 0) return []

  const children = validData.map(item => ({
    title: item.name || item.label,
    value: item.id || item.value,
    key: item.id || item.value,
  }))
  return [{
    title: selectAllLabel,
    value: 'all',
    key: 'all',
    children: children,
  }]
}

// Helper to extract filter values
const extractIds = (values) => {
  if (!Array.isArray(values)) return []
  return values.map(v => v.value || v).filter(id => id !== 'all')
}

// Sort indicator component
const SortIndicator = ({ field, currentSort }) => {
  const isActive = currentSort.field === field
  return (
    <SortIcons>
      <CaretUpOutlined className={isActive && currentSort.order === 'asc' ? 'active' : 'inactive'} />
      <CaretDownOutlined className={isActive && currentSort.order === 'desc' ? 'active' : 'inactive'} />
    </SortIcons>
  )
}

const TpoStudentListTable = ({
  instituteId,
  fetchTpoStudentList,
  exportTpoStudentListToExcel,
  students,
  pagination,
  loading,
  onBack,
  selectedYear,
}) => {
  const dispatch = useDispatch()
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [localSearchTerm, setLocalSearchTerm] = useState('')
  const [searchText, setSearchText] = useState('')
  const debounceRef = useRef(null)

  // Sorting state
  const [sortBy, setSortBy] = useState({ field: 'name', order: 'asc' })

  // Filter options (all fetched at once)
  const [domainOptions, setDomainOptions] = useState([])
  const [degreeOptions, setDegreeOptions] = useState([])
  const [departmentOptions, setDepartmentOptions] = useState([])
  const [specializationOptions, setSpecializationOptions] = useState([])

  // Selected filter values
  const [topLevelDomain, setTopLevelDomain] = useState([])
  const [topLevelDegree, setTopLevelDegree] = useState([])
  const [topLevelDepartment, setTopLevelDepartment] = useState([])
  const [topLevelSpecialization, setTopLevelSpecialization] = useState([])

  // Fetch ALL filter options at once on mount
  useEffect(() => {
    const fetchAllFilters = async () => {
      if (!instituteId) return

      // Fetch all filter options in parallel (no hierarchy)
      const [domains, degrees, departments, specializations] = await Promise.all([
        dispatch(getAssessmentDomains({ instituteId, passingYear: selectedYear || '' })),
        dispatch(getAssessmentDegrees({ instituteId, domains: [] })),
        dispatch(getAssessmentDepartments({ instituteId, degrees: [] })),
        dispatch(getAssessmentSpecializations({ instituteId, departments: [] }))
      ])

      setDomainOptions(buildTreeData(domains, 'Select All Domains'))
      setDegreeOptions(buildTreeData(degrees, 'Select All Degrees'))
      setDepartmentOptions(buildTreeData(departments, 'Select All Depts.'))
      setSpecializationOptions(buildTreeData(specializations, 'Select All Specs.'))
    }

    fetchAllFilters()
  }, [dispatch, instituteId, selectedYear])

  // Clear filters on unmount
  useEffect(() => {
    return () => {
      // Clear all filter states when component unmounts
      setTopLevelDomain([])
      setTopLevelDegree([])
      setTopLevelDepartment([])
      setTopLevelSpecialization([])
      setSearchText('')
      setLocalSearchTerm('')
    }
  }, [])

  // Fetch student data with filters
  const fetchData = useCallback((page = 1) => {
    if (!instituteId) return
    fetchTpoStudentList({
      instituteId,
      pageNumber: page,
      pageSize: 10,
      searchParams: searchText,
      passingYear: selectedYear,
      domain: extractIds(topLevelDomain),
      degree: extractIds(topLevelDegree),
      department: extractIds(topLevelDepartment),
      specialization: extractIds(topLevelSpecialization),
      sortBy,
    })
  }, [instituteId, selectedYear, searchText, topLevelDomain, topLevelDegree, topLevelDepartment, topLevelSpecialization, sortBy, fetchTpoStudentList])

  // Fetch on mount and filter changes
  useEffect(() => {
    fetchData(1)
  }, [instituteId, selectedYear, searchText, topLevelDomain, topLevelDegree, topLevelDepartment, topLevelSpecialization, sortBy])

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const handleTableChange = (paginationInfo) => {
    fetchData(paginationInfo.current)
  }

  // Debounced search handler
  const handleSearch = useCallback((e) => {
    const value = e.target.value
    setLocalSearchTerm(value)

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setSearchText(value)
    }, 500)
  }, [])

  // Sort handler
  const handleSort = useCallback((field) => {
    setSortBy(prev => ({
      field,
      order: prev.field === field && prev.order === 'asc' ? 'desc' : 'asc'
    }))
  }, [])

  // Export handler
  const [exporting, setExporting] = useState(false)
  const handleExport = async () => {
    if (!instituteId) return
    try {
      setExporting(true)
      await exportTpoStudentListToExcel({
        instituteId,
        searchParams: searchText,
        passingYear: selectedYear,
        domain: extractIds(topLevelDomain),
        degree: extractIds(topLevelDegree),
        department: extractIds(topLevelDepartment),
        specialization: extractIds(topLevelSpecialization),
        sortBy,
      })
      setExporting(false)
    } catch (error) {
      console.error('Export failed:', error)
      setExporting(false)
    }
  }

  const columns = [
    {
      title: (
        <SortHeader onClick={() => handleSort('name')}>
          NAME & EMAIL
          <SortIndicator field="name" currentSort={sortBy} />
        </SortHeader>
      ),
      dataIndex: 'name',
      key: 'name',
      width: '280px',
      fixed: 'left',
      render: (text, record) => (
        <div
          style={{ cursor: 'pointer' }}
          onClick={() => setSelectedStudent(record)}
        >
          <div style={{ fontWeight: '500', color: '#3249D7' }}>{text}</div>
          <div style={{ color: '#888', fontSize: '12px' }}>{record.email}</div>
        </div>
      ),
    },
    {
      title: 'DEGREE / DEPT',
      dataIndex: 'degree',
      key: 'degree',
      width: '250px',
      render: (text, record) => (
        <div>
          <div style={{ fontWeight: '500', color: '#000000' }}>{record.degree}</div>
          <div style={{ color: '#888', fontSize: '12px' }}>{record.department}</div>
        </div>
      ),
    },
    {
      title: 'COMM. LEVEL',
      dataIndex: 'communicationLevel',
      key: 'communicationLevel',
      width: '160px',
      render: (text, record) => (
        <div style={{ fontWeight: '500', color: '#000000' }}>
          {text}
          {record.commImprovement && (
            <span style={{ fontSize: '9px', color: '#52c41a', marginLeft: '3px', fontWeight: '600' }}>
              {record.commImprovement}
            </span>
          )}
        </div>
      )
    },
    {
      title: 'APTITUDE LEVEL',
      dataIndex: 'aptitudeLevel',
      key: 'aptitudeLevel',
      width: '160px',
      render: (text, record) => (
        <div style={{ fontWeight: '500', color: '#000000' }}>
          {text}
          {record.aptImprovement && (
            <span style={{ fontSize: '9px', color: '#52c41a', marginLeft: '3px', fontWeight: '600' }}>
              {record.aptImprovement}
            </span>
          )}
        </div>
      )
    },
    {
      title: (
        <SortHeader onClick={() => handleSort('communicationNPS')}>
          COMM. NPS (OUT OF 100)
          <SortIndicator field="communicationNPS" currentSort={sortBy} />
        </SortHeader>
      ),
      dataIndex: 'communicationNPS',
      key: 'communicationNPS',
      width: '130px',
      render: (value) => (
        <div style={{ fontWeight: '500', color: value != null ? '#000000' : '#bfbfbf' }}>
          {value != null ? value.toFixed(2) : '-'}
        </div>
      ),
    },
    {
      title: (
        <SortHeader onClick={() => handleSort('aptitudeNPS')}>
          APT. NPS (OUT OF 100)
          <SortIndicator field="aptitudeNPS" currentSort={sortBy} />
        </SortHeader>
      ),
      dataIndex: 'aptitudeNPS',
      key: 'aptitudeNPS',
      width: '130px',
      render: (value) => (
        <div style={{ fontWeight: '500', color: value != null ? '#000000' : '#bfbfbf' }}>
          {value != null ? value.toFixed(2) : '-'}
        </div>
      ),
    },
    {
      title: 'COMM. (TAKEN/SENT)',
      key: 'commAssessments',
      width: '180px',
      render: (text, record) => (
        <span style={{ fontWeight: '500', color: '#000000' }}>
          {record.commTaken} / {record.commSent}
        </span>
      ),
    },
    {
      title: 'APTITUDE (TAKEN/SENT)',
      key: 'aptAssessments',
      width: '180px',
      render: (text, record) => (
        <span style={{ fontWeight: '500', color: '#000000' }}>
          {record.aptTaken} / {record.aptSent}
        </span>
      ),
    },
  ]

  return (
    <CandidateListWrapper>
      <HeaderFullWidth>
        {/* Header section with Back button */}
        <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
          <Col>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={onBack}
                style={{ marginRight: 10 }}
              />
              <span style={{ fontSize: 18, fontWeight: 600 }}>Total Candidates List</span>
            </div>
          </Col>
        </Row>

        <FilterSection>
          <FilterInlineContainer>
            <FilterGrid>
              <FilterGroup>
                <EnhancedFilterLabel>Degree</EnhancedFilterLabel>
                <StyledFormItem>
                  <TreeSelect
                    treeData={degreeOptions}
                    value={topLevelDegree}
                    onChange={setTopLevelDegree}
                    treeCheckable={true}
                    showCheckedStrategy={TreeSelect.SHOW_PARENT}
                    placeholder="Select Degree"
                    maxTagCount={0}
                    maxTagPlaceholder={vals => <span>{vals.length}+ selected</span>}
                    treeDefaultExpandAll={true}
                    allowClear
                  />
                </StyledFormItem>
              </FilterGroup>
              <FilterGroup>
                <EnhancedFilterLabel>Department</EnhancedFilterLabel>
                <StyledFormItem>
                  <TreeSelect
                    treeData={departmentOptions}
                    value={topLevelDepartment}
                    onChange={setTopLevelDepartment}
                    treeCheckable={true}
                    showCheckedStrategy={TreeSelect.SHOW_PARENT}
                    placeholder="Select Dept."
                    maxTagCount={0}
                    maxTagPlaceholder={vals => <span>{vals.length}+ selected</span>}
                    treeDefaultExpandAll={true}
                    allowClear
                  />
                </StyledFormItem>
              </FilterGroup>
              <FilterGroup>
                <EnhancedFilterLabel>Specialization</EnhancedFilterLabel>
                <StyledFormItem>
                  <TreeSelect
                    treeData={specializationOptions}
                    value={topLevelSpecialization}
                    onChange={setTopLevelSpecialization}
                    treeCheckable={true}
                    showCheckedStrategy={TreeSelect.SHOW_PARENT}
                    placeholder="Select Spec."
                    maxTagCount={0}
                    maxTagPlaceholder={vals => <span>{vals.length}+ selected</span>}
                    treeDefaultExpandAll={true}
                    allowClear
                  />
                </StyledFormItem>
              </FilterGroup>
            </FilterGrid>
          </FilterInlineContainer>
        </FilterSection>

        <AlignedTableTop>
          <SearchSection>
            <StyledSearchInput
              placeholder="Search by Name or Email"
              prefix={<SearchOutlined style={{ color: COLORS.GRAY_LIGHT }} />}
              value={localSearchTerm}
              onChange={handleSearch}
            />
          </SearchSection>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <ShowingText>
              Showing {students?.length || 0} of{' '}
              {pagination?.totalCount || 0}
            </ShowingText>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleExport}
              loading={exporting}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                backgroundColor: '#3249d7',
                color: '#fff',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                height: '36px',
                fontWeight: 500
              }}
            >
              Export
            </Button>
          </div>
        </AlignedTableTop>

        <Spin spinning={loading}>
          <StyledTableSqare
            columns={columns}
            dataSource={students}
            pagination={false}
            rowKey="id"
            scroll={{ x: '1300px' }}
            onChange={handleTableChange}
            bordered
            locale={{
              emptyText: <NoData>No candidates found</NoData>,
            }}
          />
        </Spin>

        {pagination?.totalCount > 10 && (
          <div
            style={{
              margin: '16px',
              textAlign: 'center',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Pagination
              current={pagination.pageNumber}
              total={pagination.totalCount}
              pageSize={pagination.pageSize}
              onChange={(page, pageSize) => handleTableChange({ current: page, pageSize })}
            />
          </div>
        )}
      </HeaderFullWidth>

      <StudentProgressDrawer
        visible={!!selectedStudent}
        onClose={() => setSelectedStudent(null)}
        student={selectedStudent}
        instituteId={instituteId}
      />
    </CandidateListWrapper>
  )
}

const mapStateToProps = (state) => ({
  students: selectTpoStudentList(state),
  pagination: selectTpoStudentListPagination(state),
  loading: selectTpoStudentListLoading(state),
})

const mapDispatchToProps = {
  fetchTpoStudentList,
  exportTpoStudentListToExcel,
}

export default connect(mapStateToProps, mapDispatchToProps)(TpoStudentListTable)
